import React, { useState, useMemo } from "react";

/* =======================
   Stripe Imports
======================= */
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

/* =======================
   App / Utils
======================= */
import { update_stripe_card } from "src/DAL/Payment/Payment";
import { useSnackbar } from "notistack";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";

/* =======================
   MUI
======================= */
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";

/* =======================
   Card Preview
======================= */
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import { Navigate, useNavigate } from "react-router-dom";

/* =========================================================
   CARD FORM (Stripe Card UI + Submit)
========================================================= */
const CardForm = () => {
  const pathname = window.location.pathname;
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  /* ---------- Local States ---------- */
  const [isLoading, setIsLoading] = useState(false);
  const [cardHolderName, setCardHolderName] = useState("");
  const [cardMetadata, setCardMetadata] = useState({
    brand: "unknown",
    complete: false,
  });

  /* ---------- API Call ---------- */
  const handleCardAction = async (card_token) => {
    const result = await update_stripe_card({ source: card_token });

    if (result.code === 200) {
      if (pathname !== "/manage-card"){
        navigate("/manage-card");
      }
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }

    setIsLoading(false);
  };

  /* ---------- Form Submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsLoading(true);

    const cardElement = elements.getElement(CardElement);

    const { token, error } = await stripe.createToken(cardElement, {
      name: cardHolderName,
    });

    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
      setIsLoading(false);
      return;
    }

    handleCardAction(token.id);
  };

  /* ---------- UI ---------- */
  return (
    <div className="row mt-5">
      {/* Card Preview */}
      <div className="col-md-4 how-img mb-4 update-stripe-card">
        <Cards
          cvc=""
          expiry=""
          focused=""
          name={cardHolderName || "YOUR NAME"}
          number={cardMetadata.complete ? "•••• •••• •••• ••••" : ""}
          issuer={cardMetadata.brand}
          preview
        />
      </div>

      {/* Card Form */}
      <div className="col-md-8">
        <form onSubmit={handleSubmit}>
          {/* Card Holder Name */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}
            >
              Enter Name
            </Typography>

            <input
              type="text"
              placeholder="Name on card"
              value={cardHolderName}
              onChange={(e) => setCardHolderName(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 15px",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                fontSize: "16px",
                outline: "none",
                fontFamily: theme.typography.fontFamily,
              }}
              onFocus={(e) =>
                (e.target.style.borderColor = theme.palette.primary.main)
              }
              onBlur={(e) =>
                (e.target.style.borderColor = theme.palette.divider)
              }
            />
          </Box>

          {/* Card Details */}
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}
          >
            Card Details
          </Typography>

          <Box
            sx={{
              mb: 4,
              p: 2,
              border: "1px solid",
              borderColor: theme.palette.divider,
              borderRadius: 1.5,
              backgroundColor: alpha(
                theme.palette.background.neutral || theme.palette.grey[100],
                0.5
              ),
              "&:focus-within": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 2px ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            <CardElement
              onChange={(e) =>
                setCardMetadata({ brand: e.brand, complete: e.complete })
              }
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                    "::placeholder": {
                      color: theme.palette.text.disabled,
                    },
                  },
                  invalid: {
                    color: theme.palette.error.main,
                  },
                },
              }}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="small-contained-button"
              disabled={!stripe || isLoading}
            >
              {isLoading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  Processing...
                </Box>
              ) : (
                "Add Card"
              )}
            </button>
          </Box>
        </form>
      </div>
    </div>
  );
};

/* =========================================================
   STRIPE WRAPPER
========================================================= */
const HandleChangeCard = () => {
  const { stripeKey } = useContentSetting();
  const theme = useTheme();

  const stripePromise = useMemo(
    () => (stripeKey ? loadStripe(stripeKey) : null),
    [stripeKey]
  );

  if (!stripeKey) {
    return (
      <div className="container mt-5 text-center">
        <Typography color="error" variant="h6">
          Stripe Key is missing. Please contact support.
        </Typography>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      <h2>Enter Card Details</h2>

      <Elements stripe={stripePromise}>
        <CardForm />
      </Elements>
    </div>
  );
};

export default HandleChangeCard;
